import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { readableApiError } from '../utils/apiError';
import { CampaignPreviewDialog, CampaignReportDialog } from '../components/CampaignPreviewReport';
import {
  ArrowLeft, Mail, Send, Plus, Trash2, Loader2, FileText, X, Check, Edit, Code, Copy, ExternalLink,
  Globe, Sparkles, Search as SearchIcon, MapPin, BarChart3, AlertCircle, Zap, Radio, ClipboardEdit, Gift,
  Eye, Settings, Share2, Tag
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ApiCredentialBox from '../components/ApiCredentialBox';
import PromoPartnersTab from '../components/PromoPartnersTab';
import SwagBagTab from '../components/SwagBagTab';
import SMSBlastTab from '../components/SMSBlastTab';
import MissedUpgradesPanel from '../components/MissedUpgradesPanel';
import ShareTrackTab from '../components/ShareTrackTab';
import DiscountCodesTab from '../components/DiscountCodesTab';
import KickbackConfigTab from '../components/KickbackConfigTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventMarketing({ embedded = false, eventId: eventIdProp }) {
  const params = useParams();
  const eventId = eventIdProp || params.eventId;
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', subject: '', body_html: '', list_ids: [] });
  const [sending, setSending] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return ['campaigns', 'sms', 'syndication', 'promo', 'codes', 'swag-bag', 'share'].includes(t) ? t : 'campaigns';
    } catch { return 'campaigns'; }
  });
  const [copied, setCopied] = useState(false);

  // Widget customizer state
  const [widgetAccent, setWidgetAccent] = useState('6366f1');
  const [widgetBg, setWidgetBg] = useState('ffffff');
  const [widgetText, setWidgetText] = useState('0f172a');
  const [widgetBtnText, setWidgetBtnText] = useState('ffffff');
  const [widgetShowImage, setWidgetShowImage] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);

  // ===== Syndication state =====
  const [synTargets, setSynTargets] = useState([]);
  const [synSubmissions, setSynSubmissions] = useState([]);
  const [synStats, setSynStats] = useState({ total: 0, by_status: {} });
  const [autofill, setAutofill] = useState(null);
  const [synLoading, setSynLoading] = useState(false);
  const [synSearch, setSynSearch] = useState('');
  const [synScope, setSynScope] = useState('all'); // all | national | local
  const [pressTone, setPressTone] = useState('professional');
  const [pressAngle, setPressAngle] = useState('');
  const [pressTarget, setPressTarget] = useState('');
  const [pressGenerating, setPressGenerating] = useState(false);
  const [pressRelease, setPressRelease] = useState(null);
  const [activeSynSection, setActiveSynSection] = useState('targets'); // targets | press | autofill
  const [autoPublishTargets, setAutoPublishTargets] = useState([]);
  const [autoPublishing, setAutoPublishing] = useState(null); // target name currently publishing

  // Blast modal state
  const [blastOpen, setBlastOpen] = useState(false);
  const [emailTargets, setEmailTargets] = useState([]);
  const [blastSelected, setBlastSelected] = useState({}); // { name: bool }
  const [blastEmails, setBlastEmails] = useState({}); // { name: email override }
  const [blastSending, setBlastSending] = useState(false);
  const [blastResult, setBlastResult] = useState(null);
  // Press release preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  // Custom target management
  const [customTargetsOpen, setCustomTargetsOpen] = useState(false);
  const [customTargets, setCustomTargets] = useState([]);
  const [customTargetsLoading, setCustomTargetsLoading] = useState(false);

  useEffect(() => {
    if (!['admin', 'organization'].includes(user?.role)) { navigate('/'); return; }
    fetchEvent();
    fetchCampaigns();
    fetchLists();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API}/hosted-events/${eventId}`);
      setEvent(res.data?.event || res.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/marketing/campaigns?event_id=${eventId}`, { headers });
      setCampaigns(res.data.campaigns || []);
    } catch {}
  };

  const fetchLists = async () => {
    try {
      const res = await axios.get(`${API}/marketing/email-lists`, { headers });
      setLists(res.data.lists || []);
    } catch {}
  };

  const saveCampaign = async () => {
    if (!form.name || !form.subject) { toast.error('Name and subject required'); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/marketing/campaigns/${editingId}`, { ...form, event_id: eventId }, { headers });
        toast.success('Campaign updated');
      } else {
        await axios.post(`${API}/marketing/campaigns`, { ...form, event_id: eventId }, { headers });
        toast.success('Campaign created');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ name: '', subject: '', body_html: '', list_ids: [] });
      fetchCampaigns();
    } catch (err) { toast.error(readableApiError(err, 'Failed to save')); }
  };

  const sendCampaign = async (id) => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/marketing/campaigns/${id}/send`, {}, { headers });
      toast.success(`Sent to ${res.data.sent} subscribers`);
      fetchCampaigns();
    } catch (err) { toast.error(readableApiError(err, 'Failed to send')); }
    finally { setSending(false); }
  };

  const deleteCampaign = async (id) => {
    try {
      await axios.delete(`${API}/marketing/campaigns/${id}`, { headers });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleList = (listId) => {
    setForm(p => ({ ...p, list_ids: p.list_ids.includes(listId) ? p.list_ids.filter(id => id !== listId) : [...p.list_ids, listId] }));
  };

  // ===== Syndication helpers =====
  const cityKey = (event?.city || '').toString().toLowerCase().trim().replace(/\s+/g, '_');

  const [trackingSummary, setTrackingSummary] = useState(null);

  const fetchSyndication = async () => {
    if (!eventId) return;
    setSynLoading(true);
    try {
      const [tRes, sRes, stRes, aRes, apRes, tsRes] = await Promise.all([
        axios.get(`${API}/syndication/targets${cityKey ? `?city=${encodeURIComponent(cityKey)}` : ''}`),
        axios.get(`${API}/syndication/events/${eventId}/submissions`, { headers }),
        axios.get(`${API}/syndication/events/${eventId}/stats`, { headers }),
        axios.get(`${API}/syndication/events/${eventId}/autofill`, { headers }),
        axios.get(`${API}/syndication/auto-publish/supported`),
        axios.get(`${API}/syndication/events/${eventId}/tracking-summary`, { headers }).catch(() => ({ data: null })),
      ]);
      setSynTargets(tRes.data.targets || []);
      setSynSubmissions(sRes.data.submissions || []);
      setSynStats(stRes.data || { total: 0, by_status: {} });
      setAutofill(aRes.data || null);
      setAutoPublishTargets(apRes.data.targets || []);
      setTrackingSummary(tsRes.data || null);
    } catch (e) {
      console.error('syndication fetch failed', e);
    } finally { setSynLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'syndication' && event) fetchSyndication();
    // eslint-disable-next-line
  }, [activeTab, event?.event_id]);

  const findSubmission = (targetName) => synSubmissions.find(s => s.target_name === targetName);

  const upsertSubmission = async (target, status, postedUrl = '', notes = '') => {
    try {
      const existing = findSubmission(target.name);
      const res = await axios.post(`${API}/syndication/events/${eventId}/submissions`, {
        target_name: target.name,
        target_url: target.url,
        scope: target.scope || 'national',
        status,
        posted_url: postedUrl || existing?.posted_url || '',
        notes: notes || existing?.notes || '',
      }, { headers });
      setSynSubmissions(prev => {
        const others = prev.filter(s => s.target_name !== target.name);
        return [res.data, ...others];
      });
      // refresh stats
      const stRes = await axios.get(`${API}/syndication/events/${eventId}/stats`, { headers });
      setSynStats(stRes.data || { total: 0, by_status: {} });
    } catch (e) {
      toast.error(readableApiError(e, 'Failed to update submission'));
    }
  };

  const runAutoPublish = async (target) => {
    setAutoPublishing(target.name);
    try {
      const res = await axios.post(
        `${API}/syndication/events/${eventId}/auto-publish/${encodeURIComponent(target.name)}`,
        {},
        { headers }
      );
      if (res.data.ok) {
        toast.success(res.data.message || `Pushed to ${target.name}`);
      } else {
        toast.error(res.data.message || `Couldn't push to ${target.name}`);
      }
      await fetchSyndication();
    } catch (e) {
      toast.error(readableApiError(e, `Auto-publish to ${target.name} failed`));
    } finally {
      setAutoPublishing(null);
    }
  };

  const generatePressRelease = async () => {
    setPressGenerating(true);
    try {
      const res = await axios.post(`${API}/syndication/events/${eventId}/press-release`, {
        tone: pressTone,
        angle: pressAngle,
        target_name: pressTarget,
      }, { headers });
      setPressRelease(res.data.press_release);
      if (res.data.press_release?._fallback) {
        toast.warning('AI unavailable — generated a fallback press release');
      } else {
        toast.success('Press release generated');
      }
    } catch (e) {
      toast.error(readableApiError(e, 'Failed to generate'));
    } finally { setPressGenerating(false); }
  };

  const buildMailto = (target) => {
    if (!pressRelease || !autofill) return target.url;
    const subj = encodeURIComponent(pressRelease.email_subject || `Press Release: ${autofill.title}`);
    const bodyTxt = `Hello ${target.name} team,\n\nWe'd love to share the following event with your audience:\n\n${pressRelease.headline}\n${pressRelease.subheadline}\n\n${pressRelease.dateline}\n\n${pressRelease.body}\n\n${pressRelease.boilerplate}\n\n--\n${pressRelease.media_contact}\n\nEvent link: ${autofill.ticket_url}`;
    return `mailto:?subject=${subj}&body=${encodeURIComponent(bodyTxt)}`;
  };

  const openBlastModal = async () => {
    let pr = pressRelease;
    if (!pr) {
      // Auto-generate so the modal opens in one click
      setPressGenerating(true);
      try {
        const res = await axios.post(`${API}/syndication/events/${eventId}/press-release`, {
          tone: pressTone,
          angle: pressAngle,
          target_name: pressTarget,
        }, { headers });
        pr = res.data.press_release;
        setPressRelease(pr);
      } catch (e) {
        toast.error(readableApiError(e, 'Press release generation failed'));
        setPressGenerating(false);
        return;
      } finally { setPressGenerating(false); }
    }
    if (!pr) return;
    try {
      const res = await axios.get(`${API}/syndication/events/${eventId}/email-targets`, { headers });
      const tgs = res.data.targets || [];
      setEmailTargets(tgs);
      const sel = {}; const em = {};
      tgs.forEach(t => { sel[t.name] = true; em[t.name] = t.submit_email; });
      setBlastSelected(sel); setBlastEmails(em);
      setBlastResult(null);
      setBlastOpen(true);
    } catch (e) {
      toast.error(readableApiError(e, 'Failed to load email targets'));
    }
  };

  const sendBlast = async () => {
    const target_names = emailTargets.filter(t => blastSelected[t.name]).map(t => t.name);
    if (target_names.length === 0) { toast.error('Select at least one outlet'); return; }
    setBlastSending(true);
    try {
      const res = await axios.post(`${API}/syndication/events/${eventId}/blast-press-release`, {
        press_release: pressRelease,
        target_names,
        emails_override: blastEmails,
      }, { headers });
      setBlastResult(res.data);
      const cat = res.data.error_category;
      if (res.data.sent === 0 && cat === 'resend_daily_quota_exceeded') {
        toast.error('Resend daily quota reached — upgrade your Resend plan or wait until tomorrow.', { duration: 8000 });
      } else if (res.data.sent === 0 && cat === 'resend_rate_limit') {
        toast.error('Resend rate limit hit. Try again in a minute or send to fewer outlets.', { duration: 7000 });
      } else if (res.data.sent === 0 && cat === 'resend_domain_not_verified') {
        toast.error('Sending domain not verified in Resend. Verify press@eventism.com in your Resend dashboard.', { duration: 8000 });
      } else if (res.data.sent === 0) {
        toast.error(`Blast failed: ${res.data.results?.[0]?.error || 'all outlets rejected'}`, { duration: 7000 });
      } else if (res.data.failed > 0) {
        toast.warning(`Sent ${res.data.sent}/${res.data.total} — ${res.data.failed} failed. Check the results panel.`, { duration: 6000 });
      } else {
        toast.success(`Sent to ${res.data.sent}/${res.data.total} outlets · BCC team@eventism.com`);
      }
      // refresh submissions + stats
      const [sRes, stRes] = await Promise.all([
        axios.get(`${API}/syndication/events/${eventId}/submissions`, { headers }),
        axios.get(`${API}/syndication/events/${eventId}/stats`, { headers }),
      ]);
      setSynSubmissions(sRes.data.submissions || []);
      setSynStats(stRes.data || { total: 0, by_status: {} });
    } catch (e) {
      toast.error(readableApiError(e, 'Blast failed'));
    } finally { setBlastSending(false); }
  };

  const previewBlast = async () => {
    const target_names = emailTargets.filter(t => blastSelected[t.name]).map(t => t.name);
    if (target_names.length === 0) { toast.error('Select at least one outlet first'); return; }
    setPreviewLoading(true);
    try {
      const res = await axios.post(
        `${API}/syndication/events/${eventId}/press-release/preview`,
        { press_release: pressRelease, target_names, emails_override: blastEmails },
        { headers },
      );
      setPreviewData(res.data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(readableApiError(e, 'Preview failed'));
    } finally { setPreviewLoading(false); }
  };

  const fetchCustomTargets = async () => {
    setCustomTargetsLoading(true);
    try {
      const res = await axios.get(`${API}/syndication/custom-targets`, { headers });
      setCustomTargets(res.data.targets || []);
    } catch (e) {
      toast.error(readableApiError(e, 'Failed to load custom targets'));
    } finally { setCustomTargetsLoading(false); }
  };

  const saveCustomTarget = async (target, isNew) => {
    try {
      if (isNew) {
        await axios.post(`${API}/syndication/custom-targets`, target, { headers });
        toast.success(`Added "${target.name}"`);
      } else {
        await axios.patch(`${API}/syndication/custom-targets/${target.target_id}`, target, { headers });
        toast.success(`Updated "${target.name}"`);
      }
      await fetchCustomTargets();
      // also refresh the public target list so it shows up immediately
      const cityKey = (event?.city || '').toLowerCase().replace(/\s+/g, '_');
      const r = await axios.get(`${API}/syndication/targets${cityKey ? `?city=${encodeURIComponent(cityKey)}` : ''}`);
      setSynTargets(r.data.targets || []);
    } catch (e) {
      toast.error(readableApiError(e, 'Save failed'));
      throw e;
    }
  };

  const deleteCustomTarget = async (targetId) => {
    try {
      await axios.delete(`${API}/syndication/custom-targets/${targetId}`, { headers });
      toast.success('Custom target removed');
      await fetchCustomTargets();
      const cityKey = (event?.city || '').toLowerCase().replace(/\s+/g, '_');
      const r = await axios.get(`${API}/syndication/targets${cityKey ? `?city=${encodeURIComponent(cityKey)}` : ''}`);
      setSynTargets(r.data.targets || []);
    } catch (e) {
      toast.error(readableApiError(e, 'Delete failed'));
    }
  };

  const kindMeta = (kind) => {
    if (kind === 'api') return { label: 'API', color: 'emerald', icon: Zap };
    if (kind === 'email') return { label: 'Email', color: 'sky', icon: Mail };
    return { label: 'Form', color: 'violet', icon: ClipboardEdit };
  };

  const filteredTargets = synTargets.filter(t => {
    if (synScope !== 'all' && t.scope !== synScope) return false;
    if (synSearch && !`${t.name} ${t.category}`.toLowerCase().includes(synSearch.toLowerCase())) return false;
    return true;
  });

  const embedParams = new URLSearchParams({ accent: widgetAccent, bg: widgetBg, text: widgetText, btnText: widgetBtnText, showImage: widgetShowImage.toString() }).toString();
  const embedUrl = `${process.env.REACT_APP_BACKEND_URL}/embed/tickets/${eventId}?${embedParams}`;
  const embedCode = `<iframe id="eventism-tw-${eventId}" src="${embedUrl}" style="width:100%;max-width:560px;border:none;border-radius:14px;" title="Get Tickets - ${event?.name || 'Event'}" allow="payment"></iframe>\n<script>(function(){var f=document.getElementById("eventism-tw-${eventId}");try{var u=new URL(f.src);u.searchParams.set("purl",location.href);u.searchParams.set("pref",document.referrer||"");f.src=u.toString();}catch(e){}window.addEventListener("message",function(e){if(e.data&&e.data.type==="eventism-widget-resize"){document.querySelectorAll('iframe[src*="${eventId}"]').forEach(function(fr){fr.style.height=e.data.height+"px";});}});})();<\/script>`;

  const copyToClipboard = (text, label) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => { setCopied(true); toast.success(`${label} copied!`); setTimeout(() => setCopied(false), 2000); }).catch(() => fallbackCopy(text, label));
    } else { fallbackCopy(text, label); }
  };
  const fallbackCopy = (text, label) => {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); setCopied(true); toast.success(`${label} copied!`); setTimeout(() => setCopied(false), 2000); } catch { toast.error('Copy failed — please select and copy manually'); }
    document.body.removeChild(ta);
  };

  if (loading) return <div className={embedded ? 'p-8 flex items-center justify-center' : 'min-h-screen bg-background pt-20 flex items-center justify-center'}><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  return (
    <div className={embedded ? '' : 'min-h-screen bg-background pt-16 pb-12'}>
      <div className={embedded ? '' : 'max-w-4xl mx-auto px-4 sm:px-6'}>
        {!embedded && (
          <div className="flex items-center gap-3 mb-6 pt-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/events')} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Event Marketing</h1>
              <p className="text-sm text-muted-foreground">{event?.name || 'Event'}</p>
            </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/ticket-holders')} data-testid="go-crm">
            Manage Lists in CRM
          </Button>
        </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="campaigns" className="flex-1" data-testid="tab-campaigns"><Mail className="h-4 w-4 mr-2" /> Email Blasts</TabsTrigger>
            <TabsTrigger value="sms" className="flex-1" data-testid="tab-sms"><Radio className="h-4 w-4 mr-2" /> SMS Blasts</TabsTrigger>
            <TabsTrigger value="syndication" className="flex-1" data-testid="tab-syndication"><Globe className="h-4 w-4 mr-2" /> Syndication</TabsTrigger>
            <TabsTrigger value="promo" className="flex-1" data-testid="tab-promo"><Radio className="h-4 w-4 mr-2" /> Promo Partners</TabsTrigger>
            <TabsTrigger value="discounts" className="flex-1" data-testid="tab-discounts"><Tag className="h-4 w-4 mr-2" /> Discounts</TabsTrigger>
            <TabsTrigger value="kickbacks" className="flex-1" data-testid="tab-kickbacks"><Zap className="h-4 w-4 mr-2" /> Kickbacks</TabsTrigger>
            <TabsTrigger value="codes" className="flex-1" data-testid="tab-codes"><Code className="h-4 w-4 mr-2" /> Codes</TabsTrigger>
            <TabsTrigger value="swag-bag" className="flex-1" data-testid="tab-swag-bag"><Gift className="h-4 w-4 mr-2" /> Swag Bag</TabsTrigger>
            <TabsTrigger value="upgrades" className="flex-1" data-testid="tab-upgrades"><Sparkles className="h-4 w-4 mr-2" /> Upgrades</TabsTrigger>
            <TabsTrigger value="share" className="flex-1" data-testid="tab-share"><Share2 className="h-4 w-4 mr-2" /> Share &amp; Track</TabsTrigger>
          </TabsList>

          {/* ===== CAMPAIGNS TAB ===== */}
          <TabsContent value="campaigns">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Campaigns for this Event</h2>
              <Button size="sm" onClick={() => { setShowForm(true); setEditingId(null); setForm({ name: '', subject: '', body_html: '', list_ids: [] }); }} data-testid="new-campaign-btn">
                <Plus className="h-4 w-4 mr-1" /> New Campaign
              </Button>
            </div>

        {showForm && (
          <Card className="mb-6 border-indigo-500/20">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{editingId ? 'Edit Campaign' : 'New Email Campaign'}</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Campaign Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Early Bird Announcement" data-testid="campaign-name" /></div>
                <div className="space-y-2"><Label>Email Subject</Label><Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. You're Invited!" data-testid="campaign-subject" /></div>
              </div>
              <div className="space-y-2">
                <Label>Email Body (HTML) — Use {"{{name}}"} for personalization</Label>
                <Textarea value={form.body_html} onChange={e => setForm(p => ({ ...p, body_html: e.target.value }))} placeholder="<h2>Hey {{name}}!</h2><p>You're invited...</p>" className="min-h-[150px] font-mono text-sm" data-testid="campaign-body" />
              </div>
              <div className="space-y-2">
                <Label>Target Lists <span className="text-xs text-muted-foreground">(manage lists in <button className="text-indigo-400 underline" onClick={() => navigate('/admin/ticket-holders')}>CRM</button>)</span></Label>
                {lists.length === 0 ? <p className="text-xs text-muted-foreground">No lists yet. Go to CRM to create lists first.</p> : (
                  <div className="space-y-1.5">{lists.map(l => (
                    <label key={l.list_id} className="flex items-center gap-2 p-2 rounded border border-border cursor-pointer hover:bg-muted/30">
                      <input type="checkbox" checked={form.list_ids.includes(l.list_id)} onChange={() => toggleList(l.list_id)} className="rounded" />
                      <span className="text-sm flex-1">{l.name}</span>
                      <Badge variant="outline" className="text-[10px]">{l.subscriber_count} subs</Badge>
                    </label>
                  ))}</div>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={saveCampaign} data-testid="save-campaign"><FileText className="h-4 w-4 mr-1" /> {editingId ? 'Update' : 'Save Draft'}</Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3" data-testid="campaign-list">
          {campaigns.length === 0 ? (
            <Card className="text-center py-10"><CardContent><Mail className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No campaigns for this event yet</p></CardContent></Card>
          ) : campaigns.map(c => (
            <Card key={c.campaign_id} className="border-border hover:border-indigo-500/30 transition-colors">
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.status === 'sent' ? 'bg-emerald-500/10' : c.status === 'sending' ? 'bg-amber-500/10' : 'bg-indigo-500/10'}`}>
                  {c.status === 'sent' ? <Check className="h-5 w-5 text-emerald-400" /> : c.status === 'sending' ? <Loader2 className="h-5 w-5 text-amber-400 animate-spin" /> : <Mail className="h-5 w-5 text-indigo-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">Subject: {c.subject}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <Badge variant={c.status === 'sent' ? 'default' : 'outline'} className="text-[10px] capitalize">{c.status}</Badge>
                    {c.status === 'sent' && <span className="text-[10px] text-muted-foreground">Sent {c.stats?.sent || 0} / {c.stats?.total || 0}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setPreviewId(c.campaign_id)} title="Preview email" data-testid={`preview-${c.campaign_id}`}><Eye className="h-3.5 w-3.5" /></Button>
                  {c.status === 'sent' && (
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => setReportId(c.campaign_id)} data-testid={`report-${c.campaign_id}`}>
                      <BarChart3 className="h-3.5 w-3.5 mr-1" /> Report
                    </Button>
                  )}
                  {c.status === 'draft' && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(c.campaign_id); setForm({ name: c.name, subject: c.subject, body_html: c.body_html, list_ids: c.list_ids || [] }); setShowForm(true); }}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={() => setPreviewId(c.campaign_id)} disabled={sending} data-testid={`send-${c.campaign_id}`}><Send className="h-3.5 w-3.5 mr-1" /> Send</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteCampaign(c.campaign_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <CampaignPreviewDialog
          campaignId={previewId}
          open={!!previewId}
          onOpenChange={(v) => !v && setPreviewId(null)}
          token={token}
          canSend={campaigns.find(c => c.campaign_id === previewId)?.status === 'draft'}
          onSend={() => sendCampaign(previewId)}
        />
        <CampaignReportDialog
          campaignId={reportId}
          open={!!reportId}
          onOpenChange={(v) => !v && setReportId(null)}
          token={token}
        />
          </TabsContent>

          {/* ===== SMS BLASTS TAB ===== */}
          <TabsContent value="sms">
            <SMSBlastTab event={event} token={token} />
          </TabsContent>

          {/* ===== SYNDICATION TAB ===== */}
          <TabsContent value="syndication">
            <div className="space-y-6" data-testid="syndication-panel">
              {/* Header & Stats */}
              <div>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2"><Globe className="h-5 w-5 text-indigo-400" /> Event Syndication</h2>
                    <p className="text-sm text-muted-foreground">Distribute this event to {synTargets.length}+ high-DA event sites, calendars and media outlets {event?.city ? `in ${event.city}` : ''}.</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <Badge variant="outline" className="bg-amber-500/10 border-amber-500/30">Pending {synStats.by_status?.pending || 0}</Badge>
                    <Badge variant="outline" className="bg-indigo-500/10 border-indigo-500/30">Submitted {synStats.by_status?.submitted || 0}</Badge>
                    <Badge variant="outline" className="bg-emerald-500/10 border-emerald-500/30">Live {synStats.by_status?.live || 0}</Badge>
                    {trackingSummary?.summary?.opened > 0 && (
                      <Badge variant="outline" className="bg-cyan-500/10 border-cyan-500/30 text-cyan-300" data-testid="syn-opened-badge">
                        Opened {trackingSummary.summary.opened}
                      </Badge>
                    )}
                    {trackingSummary?.summary?.bounced > 0 && (
                      <Badge variant="outline" className="bg-rose-500/10 border-rose-500/30 text-rose-300" data-testid="syn-bounced-badge">
                        Bounced {trackingSummary.summary.bounced}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Resend tracking detail (when at least one press blast has been sent) */}
                {trackingSummary?.rows?.length > 0 && (
                  <Card className="bg-card/40 border-border/40" data-testid="press-tracking-card">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-cyan-400" />
                          <h3 className="text-sm font-semibold">Press Release Tracking</h3>
                        </div>
                        <div className="flex gap-3 text-[11px] text-muted-foreground">
                          <span>Sent <strong className="text-foreground">{trackingSummary.summary.sent}</strong></span>
                          <span>Delivered <strong className="text-emerald-400">{trackingSummary.summary.delivered}</strong></span>
                          <span>Opened <strong className="text-cyan-400">{trackingSummary.summary.opened}</strong></span>
                          <span>Clicked <strong className="text-indigo-400">{trackingSummary.summary.clicked}</strong></span>
                          <span>Bounced <strong className="text-rose-400">{trackingSummary.summary.bounced}</strong></span>
                        </div>
                      </div>
                      <div className="text-[11px] text-muted-foreground mb-2">
                        Live tracking via Resend webhooks. Configure <span className="font-mono">RESEND_WEBHOOK_SECRET</span> in backend env to enable signature verification.
                      </div>
                      <div className="space-y-1 max-h-56 overflow-y-auto">
                        {trackingSummary.rows.map(r => {
                          const opened = !!r.opened_at;
                          const bounced = !!r.bounced_at;
                          const delivered = !!r.delivered_at;
                          return (
                            <div key={r.target_name} className="flex items-center gap-2 py-1 px-2 rounded text-xs hover:bg-muted/30">
                              <span className="font-medium truncate flex-1">{r.target_name}</span>
                              <span className="font-mono text-[10px] text-muted-foreground truncate">{r.submit_email}</span>
                              {bounced ? (
                                <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[9px] py-0 px-1.5">BOUNCED</Badge>
                              ) : opened ? (
                                <Badge className="bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-[9px] py-0 px-1.5">
                                  OPENED · {r.open_count || 1}×
                                </Badge>
                              ) : delivered ? (
                                <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[9px] py-0 px-1.5">DELIVERED</Badge>
                              ) : (
                                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[9px] py-0 px-1.5">SENT</Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Prominent Press-Release Blast CTA */}
                <div className="mt-4 p-4 rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-indigo-500/5 flex items-center gap-3 flex-wrap" data-testid="syn-blast-cta">
                  <div className="p-2.5 rounded-lg bg-emerald-500/20 flex-shrink-0">
                    <Radio className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-semibold">One-click Press Release Blast</p>
                    <p className="text-xs text-muted-foreground">Auto-generate an AI press release and email it to all email-capable outlets via Resend.</p>
                  </div>
                  <Button onClick={openBlastModal} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="syn-quick-blast-btn">
                    {pressGenerating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Preparing…</> : <><Send className="h-4 w-4 mr-1.5" /> Send Press Release Blast</>}
                  </Button>
                </div>
              </div>

              {/* Sub-tabs */}
              <div className="flex gap-1 p-1 bg-muted/40 rounded-lg w-fit">
                <button data-testid="syn-section-targets" onClick={() => setActiveSynSection('targets')} className={`px-3 py-1.5 rounded text-xs font-medium ${activeSynSection === 'targets' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>Sites ({filteredTargets.length})</button>
                <button data-testid="syn-section-press" onClick={() => setActiveSynSection('press')} className={`px-3 py-1.5 rounded text-xs font-medium ${activeSynSection === 'press' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>AI Press Release</button>
                <button data-testid="syn-section-autofill" onClick={() => setActiveSynSection('autofill')} className={`px-3 py-1.5 rounded text-xs font-medium ${activeSynSection === 'autofill' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>Autofill Data</button>
              </div>

              {synLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading targets…</div>}

              {/* ----- SITES SECTION ----- */}
              {activeSynSection === 'targets' && (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[180px]">
                      <SearchIcon className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                      <Input value={synSearch} onChange={e => setSynSearch(e.target.value)} placeholder="Search sites…" className="pl-8 h-8 text-sm" data-testid="syn-search" />
                    </div>
                    <div className="flex gap-1 p-1 bg-muted/40 rounded-md text-xs">
                      {['all', 'national', 'local'].map(s => (
                        <button key={s} onClick={() => setSynScope(s)} className={`px-2.5 py-1 rounded capitalize ${synScope === s ? 'bg-background shadow-sm' : 'text-muted-foreground'}`} data-testid={`syn-scope-${s}`}>
                          {s}{s === 'local' && cityKey ? ` · ${event?.city}` : ''}
                        </button>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => { setCustomTargetsOpen(true); fetchCustomTargets(); }}
                      data-testid="manage-custom-targets-btn"
                    >
                      <Settings className="h-3.5 w-3.5 mr-1" /> Manage Sites
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {filteredTargets.length === 0 && !synLoading ? (
                      <Card className="text-center py-10"><CardContent><Globe className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No targets match your filters</p></CardContent></Card>
                    ) : filteredTargets.map(t => {
                      const sub = findSubmission(t.name);
                      const status = sub?.status || 'pending';
                      const statusColor = status === 'live' ? 'emerald' : status === 'submitted' ? 'indigo' : status === 'declined' ? 'rose' : 'amber';
                      const km = kindMeta(t.kind);
                      const KIcon = km.icon;
                      return (
                        <Card key={`${t.scope}-${t.name}`} className="border-border" data-testid={`syn-target-${t.name.replace(/\W+/g, '-').toLowerCase()}`}>
                          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                            <div className={`w-9 h-9 rounded-md bg-${km.color}-500/10 flex items-center justify-center flex-shrink-0`}>
                              <KIcon className={`h-4 w-4 text-${km.color}-400`} />
                            </div>
                            <div className="flex-1 min-w-[180px]">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-sm">{t.name}</p>
                                <Badge variant="outline" className={`text-[10px] bg-${km.color}-500/10 border-${km.color}-500/30 uppercase font-semibold`} data-testid={`syn-kind-${t.name.replace(/\W+/g, '-').toLowerCase()}`}>{km.label}</Badge>
                                {t.kind === 'api' && t.is_configured && (
                                  <Badge variant="outline" className="text-[10px] bg-emerald-500/10 border-emerald-500/30 text-emerald-500" data-testid={`syn-connected-${t.name.replace(/\W+/g, '-').toLowerCase()}`}>Connected ✓</Badge>
                                )}
                                <Badge variant="outline" className="text-[10px]">DA {t.da}</Badge>
                                <Badge variant="outline" className={`text-[10px] capitalize bg-${statusColor}-500/10 border-${statusColor}-500/30`}>{status}</Badge>
                                {t.scope === 'local' && <Badge variant="outline" className="text-[10px]"><MapPin className="h-2.5 w-2.5 mr-0.5" />Local</Badge>}
                              </div>
                              <p className="text-[11px] text-muted-foreground capitalize">{t.category}{t.audience ? ` · ${t.audience}` : ''}{t.submit_email ? ` · ${t.submit_email}` : ''}</p>
                              {sub?.posted_url && (
                                <a href={sub.posted_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-emerald-400 hover:underline flex items-center gap-1 mt-0.5">
                                  <ExternalLink className="h-2.5 w-2.5" /> Live link
                                </a>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <a href={t.url} target="_blank" rel="noopener noreferrer">
                                <Button size="sm" variant="outline" className="text-xs h-8" data-testid={`syn-open-${t.name.replace(/\W+/g, '-').toLowerCase()}`}>
                                  <ExternalLink className="h-3 w-3 mr-1" /> Open
                                </Button>
                              </a>
                              {t.kind === 'email' && pressRelease && (
                                <a href={buildMailto(t)} target="_blank" rel="noopener noreferrer">
                                  <Button size="sm" variant="outline" className="text-xs h-8">
                                    <Mail className="h-3 w-3 mr-1" /> Email PR
                                  </Button>
                                </a>
                              )}
                              {t.kind === 'api' && t.is_configured && autoPublishTargets.includes(t.api_credential_target) && (
                                <Button
                                  size="sm"
                                  className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                                  disabled={autoPublishing === t.name}
                                  onClick={() => runAutoPublish(t)}
                                  data-testid={`syn-auto-publish-${t.name.replace(/\W+/g, '-').toLowerCase()}`}
                                >
                                  {autoPublishing === t.name ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                                  Auto-publish
                                </Button>
                              )}
                              <select
                                value={status}
                                onChange={e => upsertSubmission(t, e.target.value)}
                                className="text-xs h-8 rounded border border-border bg-background px-2"
                                data-testid={`syn-status-${t.name.replace(/\W+/g, '-').toLowerCase()}`}
                              >
                                <option value="pending">Pending</option>
                                <option value="submitted">Submitted</option>
                                <option value="live">Live</option>
                                <option value="declined">Declined</option>
                              </select>
                              {(status === 'submitted' || status === 'live') && (
                                <Input
                                  value={sub?.posted_url || ''}
                                  onChange={e => upsertSubmission(t, status, e.target.value)}
                                  placeholder="Live URL…"
                                  className="text-xs h-8 w-44"
                                />
                              )}
                            </div>
                            {/* API credentials box for api-kind targets */}
                            {t.kind === 'api' && t.api_credential_target && (
                              <div className="w-full">
                                <ApiCredentialBox
                                  targetName={t.api_credential_target}
                                  token={token}
                                  onSaved={fetchSyndication}
                                />
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ----- PRESS RELEASE SECTION ----- */}
              {activeSynSection === 'press' && (
                <div className="space-y-4">
                  <Card className="border-indigo-500/20">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-indigo-400" />
                        <h3 className="font-semibold text-sm">AI Press Release Generator</h3>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Tone</Label>
                          <select value={pressTone} onChange={e => setPressTone(e.target.value)} className="w-full text-xs h-9 rounded border border-border bg-background px-2" data-testid="press-tone">
                            <option value="professional">Professional</option>
                            <option value="exciting">Exciting</option>
                            <option value="formal">Formal / AP-style</option>
                            <option value="casual">Casual</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Outlet (optional)</Label>
                          <Input value={pressTarget} onChange={e => setPressTarget(e.target.value)} placeholder="e.g. Westword" className="h-9 text-xs" data-testid="press-target" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Newsworthy Angle (optional)</Label>
                          <Input value={pressAngle} onChange={e => setPressAngle(e.target.value)} placeholder="e.g. first-of-its-kind, charity" className="h-9 text-xs" data-testid="press-angle" />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={generatePressRelease} disabled={pressGenerating} size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="press-generate-btn">
                          {pressGenerating ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating…</> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Generate Press Release</>}
                        </Button>
                        {pressRelease && (
                          <Button onClick={openBlastModal} size="sm" variant="outline" className="border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10" data-testid="blast-open-btn">
                            <Radio className="h-3.5 w-3.5 mr-1" /> Send Press Email Blast
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {pressRelease && (
                    <Card className="border-border" data-testid="press-result">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-1 flex-1">
                            <h2 className="text-xl font-bold">{pressRelease.headline}</h2>
                            <p className="text-sm text-muted-foreground">{pressRelease.subheadline}</p>
                            <p className="text-xs font-mono text-indigo-400">{pressRelease.dateline}</p>
                          </div>
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => copyToClipboard(`${pressRelease.headline}\n${pressRelease.subheadline}\n\n${pressRelease.dateline}\n\n${pressRelease.body}\n\n${pressRelease.boilerplate}\n\n${pressRelease.media_contact}`, 'Press release')} data-testid="press-copy-btn">
                            <Copy className="h-3 w-3 mr-1" /> Copy
                          </Button>
                        </div>
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed border-t border-border pt-3" data-testid="press-body">{pressRelease.body}</div>
                        <div className="border-t border-border pt-3 space-y-2">
                          <div>
                            <p className="text-[11px] font-semibold uppercase text-muted-foreground">Boilerplate</p>
                            <p className="text-xs">{pressRelease.boilerplate}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold uppercase text-muted-foreground">Media Contact</p>
                            <p className="text-xs whitespace-pre-wrap">{pressRelease.media_contact}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold uppercase text-muted-foreground">Social Post</p>
                            <p className="text-xs italic">{pressRelease.social_post}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold uppercase text-muted-foreground">Outreach Email Subject</p>
                            <p className="text-xs font-mono">{pressRelease.email_subject}</p>
                          </div>
                        </div>
                        {pressRelease._fallback && (
                          <div className="text-[11px] text-amber-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Fallback content (AI unavailable). Edit before sending.</div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* ----- AUTOFILL SECTION ----- */}
              {activeSynSection === 'autofill' && autofill && (
                <Card className="border-border" data-testid="autofill-panel">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-sm flex items-center gap-2"><FileText className="h-4 w-4 text-indigo-400" /> Autofill Data</h3>
                        <p className="text-xs text-muted-foreground">Copy any field below and paste into a syndication site's submission form.</p>
                      </div>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => copyToClipboard(JSON.stringify(autofill, null, 2), 'JSON payload')} data-testid="autofill-copy-json">
                        <Copy className="h-3 w-3 mr-1" /> Copy JSON
                      </Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {Object.entries(autofill).filter(([k, v]) => typeof v !== 'object' || v === null).map(([k, v]) => (
                        <div key={k} className="border border-border rounded p-2.5 bg-muted/20">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground">{k.replace(/_/g, ' ')}</p>
                              <p className="text-xs break-words">{String(v ?? '—') || '—'}</p>
                            </div>
                            <Button size="icon" variant="ghost" className="h-6 w-6 flex-shrink-0" onClick={() => copyToClipboard(String(v ?? ''), k)} data-testid={`autofill-copy-${k}`}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {autofill.ticket_types?.length > 0 && (
                      <div className="border-t border-border pt-3">
                        <p className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">Ticket Tiers</p>
                        <div className="flex flex-wrap gap-2">
                          {autofill.ticket_types.map((t, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{t.name} · ${t.price}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* ===== PROMO PARTNERS TAB ===== */}
          <TabsContent value="promo">
            <PromoPartnersTab eventId={eventId} token={token} event={event} />
          </TabsContent>

          <TabsContent value="discounts">
            <DiscountCodesTab eventId={eventId} token={token} event={event} />
          </TabsContent>

          <TabsContent value="kickbacks">
            <KickbackConfigTab eventId={eventId} token={token} />
          </TabsContent>

          {/* ===== MISSED UPGRADES TAB ===== */}
          <TabsContent value="upgrades">
            <MissedUpgradesPanel eventId={eventId} />
          </TabsContent>

          {/* ===== CODES TAB ===== */}
          <TabsContent value="codes">
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Embed Ticket Widget</h2>
                <p className="text-sm text-muted-foreground">Customize and embed the ticket widget on any HTML page.</p>
              </div>

              <div className="grid lg:grid-cols-5 gap-6">
                {/* Customizer Panel */}
                <div className="lg:col-span-2 space-y-4">
                  <Card className="border-border">
                    <CardContent className="p-4 space-y-4">
                      <h3 className="text-sm font-semibold">Widget Customizer</h3>

                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Accent Color</Label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={`#${widgetAccent}`} onChange={e => setWidgetAccent(e.target.value.replace('#', ''))} className="w-8 h-8 rounded border border-border cursor-pointer" data-testid="color-accent" />
                            <Input value={`#${widgetAccent}`} onChange={e => setWidgetAccent(e.target.value.replace('#', ''))} className="h-8 text-xs font-mono flex-1" />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Background Color</Label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={`#${widgetBg}`} onChange={e => setWidgetBg(e.target.value.replace('#', ''))} className="w-8 h-8 rounded border border-border cursor-pointer" data-testid="color-bg" />
                            <Input value={`#${widgetBg}`} onChange={e => setWidgetBg(e.target.value.replace('#', ''))} className="h-8 text-xs font-mono flex-1" />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Text Color</Label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={`#${widgetText}`} onChange={e => setWidgetText(e.target.value.replace('#', ''))} className="w-8 h-8 rounded border border-border cursor-pointer" data-testid="color-text" />
                            <Input value={`#${widgetText}`} onChange={e => setWidgetText(e.target.value.replace('#', ''))} className="h-8 text-xs font-mono flex-1" />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Button Text Color</Label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={`#${widgetBtnText}`} onChange={e => setWidgetBtnText(e.target.value.replace('#', ''))} className="w-8 h-8 rounded border border-border cursor-pointer" data-testid="color-btn-text" />
                            <Input value={`#${widgetBtnText}`} onChange={e => setWidgetBtnText(e.target.value.replace('#', ''))} className="h-8 text-xs font-mono flex-1" />
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-1">
                          <Label className="text-xs">Show Event Image</Label>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={widgetShowImage} onChange={e => setWidgetShowImage(e.target.checked)} className="sr-only peer" data-testid="toggle-image" />
                            <div className="w-9 h-5 bg-gray-300 peer-checked:bg-indigo-500 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                          </label>
                        </div>
                      </div>

                      <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => setPreviewKey(k => k + 1)} data-testid="refresh-preview">Refresh Preview</Button>

                      <div className="grid grid-cols-3 gap-2 pt-1">
                        <button className="text-[10px] p-1.5 rounded border border-border hover:border-indigo-500/30" onClick={() => { setWidgetAccent('6366f1'); setWidgetBg('ffffff'); setWidgetText('0f172a'); setWidgetBtnText('ffffff'); }}>Default</button>
                        <button className="text-[10px] p-1.5 rounded border border-border hover:border-indigo-500/30" onClick={() => { setWidgetAccent('f97316'); setWidgetBg('fffbf5'); setWidgetText('1c1917'); setWidgetBtnText('ffffff'); }}>Eventism</button>
                        <button className="text-[10px] p-1.5 rounded border border-border hover:border-indigo-500/30" onClick={() => { setWidgetAccent('8b5cf6'); setWidgetBg('0f0b1a'); setWidgetText('e2e8f0'); setWidgetBtnText('ffffff'); }}>Dark</button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Embed Code */}
                  <Card className="border-border">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold">Embed Code</h3>
                        <Button size="sm" variant={copied ? 'default' : 'outline'} onClick={() => copyToClipboard(embedCode, 'Embed code')} className="text-xs" data-testid="copy-embed-btn">
                          {copied ? <><Check className="h-3 w-3 mr-1" /> Copied</> : <><Copy className="h-3 w-3 mr-1" /> Copy Code</>}
                        </Button>
                      </div>
                      <pre className="p-3 bg-[#0f172a] text-[#94a3b8] rounded-lg text-[10px] overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed max-h-40" data-testid="embed-code-block">{embedCode}</pre>
                    </CardContent>
                  </Card>

                  {/* Direct URL */}
                  <Card className="border-border">
                    <CardContent className="p-4 space-y-2">
                      <h3 className="text-sm font-semibold">Direct Widget URL</h3>
                      <div className="flex items-center gap-2">
                        <Input value={embedUrl} readOnly className="text-[10px] font-mono" data-testid="direct-url" />
                        <Button size="sm" variant="outline" onClick={() => copyToClipboard(embedUrl, 'URL')} data-testid="copy-url-btn"><Copy className="h-3.5 w-3.5" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Live Preview */}
                <div className="lg:col-span-3">
                  <Card className="border-border overflow-hidden sticky top-20">
                    <CardContent className="p-0">
                      <div className="p-3 bg-muted/30 border-b border-border flex items-center justify-between">
                        <span className="text-xs font-semibold text-muted-foreground">Live Preview</span>
                        <a href={embedUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 flex items-center gap-1 hover:underline" data-testid="preview-link">Open full <ExternalLink className="h-3 w-3" /></a>
                      </div>
                      <div className="p-4 flex justify-center" style={{ background: '#f0f0f0' }}>
                        <iframe key={previewKey} src={embedUrl} style={{ width: '100%', maxWidth: 560, minHeight: 500, border: 'none', borderRadius: 14 }} title="Ticket Widget Preview" data-testid="embed-preview" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ===== SWAG BAG TAB ===== */}
          <TabsContent value="swag-bag">
            <SwagBagTab eventId={eventId} token={token} />
          </TabsContent>

          {/* ===== SHARE & TRACK TAB ===== */}
          <TabsContent value="share">
            <ShareTrackTab eventId={eventId} />
          </TabsContent>
        </Tabs>

        {/* ===== PRESS RELEASE BLAST MODAL ===== */}
        {blastOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !blastSending && setBlastOpen(false)} data-testid="blast-modal">
            <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-border">
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2"><Radio className="h-5 w-5 text-emerald-500" /> Press Release Blast</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Email your AI press release to {emailTargets.length} email-capable outlets via Resend.</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => !blastSending && setBlastOpen(false)} disabled={blastSending}><X className="h-4 w-4" /></Button>
              </div>

              {!blastResult ? (
                <>
                  <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs">
                      <span className="font-semibold">{Object.values(blastSelected).filter(Boolean).length}</span> of {emailTargets.length} selected
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { const s = {}; emailTargets.forEach(t => s[t.name] = true); setBlastSelected(s); }} data-testid="blast-select-all">Select All</Button>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setBlastSelected({})} data-testid="blast-deselect-all">Deselect All</Button>
                    </div>
                  </div>
                  <div className="overflow-y-auto flex-1 px-5 py-3 space-y-2">
                    {emailTargets.map(t => (
                      <div key={t.name} className="border border-border rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`blast-row-${t.name.replace(/\W+/g, '-').toLowerCase()}`}>
                        <input type="checkbox" checked={!!blastSelected[t.name]} onChange={e => setBlastSelected(p => ({ ...p, [t.name]: e.target.checked }))} className="w-4 h-4 rounded" data-testid={`blast-check-${t.name.replace(/\W+/g, '-').toLowerCase()}`} />
                        <div className="flex-1 min-w-[160px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium">{t.name}</p>
                            <Badge variant="outline" className="text-[10px]">DA {t.da}</Badge>
                            {t.scope === 'local' && <Badge variant="outline" className="text-[10px]">Local</Badge>}
                          </div>
                          <p className="text-[10px] capitalize text-muted-foreground">{t.category}</p>
                        </div>
                        <Input
                          value={blastEmails[t.name] ?? ''}
                          onChange={e => setBlastEmails(p => ({ ...p, [t.name]: e.target.value }))}
                          placeholder="email@outlet.com"
                          className="text-xs h-8 w-64 font-mono"
                          data-testid={`blast-email-${t.name.replace(/\W+/g, '-').toLowerCase()}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5 border-t border-border bg-amber-500/5">
                    <div className="flex items-start gap-2 mb-3">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-[11px] text-muted-foreground">
                        These are best-known public press/calendar contacts. A copy of every blast is BCC'd to <span className="font-mono">team@eventism.com</span>. Sender is <span className="font-mono">onboarding@resend.dev</span> until <span className="font-mono">PRESS_RELEASE_FROM</span> env var is set to your verified domain.
                      </p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setBlastOpen(false)} disabled={blastSending || previewLoading}>Cancel</Button>
                      <Button variant="outline" size="sm" onClick={previewBlast} disabled={blastSending || previewLoading} data-testid="blast-preview-btn">
                        {previewLoading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Previewing…</> : <><Eye className="h-3.5 w-3.5 mr-1" /> Preview Press Release</>}
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={sendBlast} disabled={blastSending || previewLoading} data-testid="blast-send-btn">
                        {blastSending ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Sending…</> : <><Send className="h-3.5 w-3.5 mr-1" /> Send Blast</>}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3" data-testid="blast-result">
                  <div className={`flex items-center gap-3 p-3 rounded-lg border ${blastResult.sent > 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'}`}>
                    {blastResult.sent > 0 ? <Check className="h-5 w-5 text-emerald-500" /> : <X className="h-5 w-5 text-rose-500" />}
                    <div>
                      <p className="text-sm font-semibold">Blast complete: {blastResult.sent}/{blastResult.total} sent</p>
                      {blastResult.failed > 0 && <p className="text-xs text-rose-400">{blastResult.failed} failed</p>}
                    </div>
                  </div>
                  {blastResult.sent === 0 && blastResult.error_category === 'resend_daily_quota_exceeded' && (
                    <div className="text-xs p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200" data-testid="blast-error-quota">
                      <p className="font-semibold mb-1">Resend daily quota exceeded</p>
                      <p>Your Resend account has hit its daily email cap. Upgrade your Resend plan, or wait until the quota resets (00:00 UTC).</p>
                    </div>
                  )}
                  {blastResult.sent === 0 && blastResult.error_category === 'resend_rate_limit' && (
                    <div className="text-xs p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200" data-testid="blast-error-ratelimit">
                      <p className="font-semibold mb-1">Resend rate limit hit (5 req/sec)</p>
                      <p>Try again in 60 seconds, or split your blast into smaller batches.</p>
                    </div>
                  )}
                  {blastResult.sent === 0 && blastResult.error_category === 'resend_domain_not_verified' && (
                    <div className="text-xs p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200" data-testid="blast-error-domain">
                      <p className="font-semibold mb-1">Sender domain not verified</p>
                      <p>Verify <code>press@eventism.com</code> in your Resend dashboard → Domains, or switch the press release sender to <code>onboarding@resend.dev</code>.</p>
                    </div>
                  )}
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {(blastResult.results || []).map((r, i) => (
                      <div key={i} className="text-xs flex items-center gap-2 p-2 rounded border border-border">
                        {r.status === 'sent' ? <Check className="h-3 w-3 text-emerald-500" /> : r.status === 'failed' ? <X className="h-3 w-3 text-rose-500" /> : <AlertCircle className="h-3 w-3 text-amber-500" />}
                        <span className="font-medium">{r.target}</span>
                        <span className="text-muted-foreground font-mono">{r.email}</span>
                        <span className="ml-auto capitalize text-[10px]">{r.status}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button size="sm" onClick={() => setBlastOpen(false)} data-testid="blast-close-btn">Done</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {previewOpen && previewData && (
          <PressReleasePreviewDialog data={previewData} onClose={() => setPreviewOpen(false)} />
        )}
        {customTargetsOpen && (
          <CustomTargetsDialog
            targets={customTargets}
            loading={customTargetsLoading}
            onClose={() => setCustomTargetsOpen(false)}
            onSave={saveCustomTarget}
            onDelete={deleteCustomTarget}
            currentCity={(event?.city || '').toLowerCase().replace(/\s+/g, '_')}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================
// PRESS RELEASE PREVIEW DIALOG
// ============================================================
function PressReleasePreviewDialog({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="press-preview-modal">
      <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col border border-border" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><Eye className="h-4 w-4 text-indigo-400" /> Preview Press Release</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Will send to <strong>{data.recipient_count}</strong> outlet{data.recipient_count === 1 ? '' : 's'} · BCC <span className="font-mono">{data.team_cc}</span>
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div className="text-xs">
            <span className="text-muted-foreground">Subject:</span>
            <span className="ml-2 font-medium" data-testid="preview-subject">{data.subject}</span>
          </div>
          <div className="border border-border rounded bg-white text-black">
            <iframe
              title="Press release preview"
              srcDoc={data.html}
              className="w-full"
              style={{ height: '480px', border: 'none' }}
              data-testid="preview-iframe"
            />
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Plain-text version</summary>
            <pre className="mt-2 p-3 bg-muted/40 rounded whitespace-pre-wrap font-mono text-[11px]">{data.plain}</pre>
          </details>
          <details className="text-xs" open>
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Recipients ({data.recipient_count})</summary>
            <div className="mt-2 space-y-1 max-h-44 overflow-y-auto">
              {(data.recipients || []).map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-muted/40">
                  <span className="font-medium">{r.target}</span>
                  {r.is_custom && <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-violet-500/40 text-violet-300">CUSTOM</Badge>}
                  <span className="ml-auto font-mono text-muted-foreground">{r.email}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
        <div className="p-4 border-t border-border flex justify-end">
          <Button size="sm" onClick={onClose} data-testid="preview-close-btn">Close Preview</Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CUSTOM SYNDICATION TARGET MANAGER
// ============================================================
function CustomTargetsDialog({ targets, loading, onClose, onSave, onDelete, currentCity }) {
  const [editing, setEditing] = useState(null); // null | object (new or existing)
  const blank = { name: '', url: '', kind: 'form', submit_email: '', category: '', audience: '', da: 50, scope: 'national', city: '' };
  const startNew = () => setEditing({ ...blank });
  const startEdit = (t) => setEditing({ ...t });

  const handleSave = async () => {
    const isNew = !editing.target_id;
    if (!editing.name?.trim() || !editing.url?.trim()) {
      toast.error('Name and URL are required'); return;
    }
    if (editing.kind === 'email' && !editing.submit_email?.includes('@')) {
      toast.error('Valid submit_email required for email targets'); return;
    }
    try {
      await onSave(editing, isNew);
      setEditing(null);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !editing && onClose()} data-testid="custom-targets-modal">
      <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col border border-border" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><Settings className="h-4 w-4 text-emerald-400" /> Manage Syndication Sites</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Add new outlets, edit existing entries, or deactivate ones you no longer use. Curated sites cannot be deleted but you can override their entry by adding a custom one with the same name.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>

        {!editing ? (
          <>
            <div className="px-5 pt-3 pb-2">
              <Button size="sm" onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-custom-target-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add a Site
              </Button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 pb-5">
              {loading ? (
                <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
              ) : targets.length === 0 ? (
                <Card className="text-center py-10"><CardContent>
                  <Globe className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No custom sites yet. Click "Add a Site" to get started.</p>
                </CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {targets.map(t => (
                    <div key={t.target_id} className={`flex items-center gap-3 p-3 rounded-md border ${t.is_active ? 'border-border' : 'border-border/40 opacity-60'}`} data-testid={`custom-target-row-${t.target_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate">{t.name}</span>
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5">{t.kind}</Badge>
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5">{t.scope}{t.city ? ` · ${t.city}` : ''}</Badge>
                          {!t.is_active && <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-rose-500/40 text-rose-300">INACTIVE</Badge>}
                        </div>
                        <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-indigo-400 truncate block">{t.url}</a>
                        {t.submit_email && <div className="text-xs font-mono text-muted-foreground">{t.submit_email}</div>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => startEdit(t)} data-testid={`edit-target-${t.target_id}`}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" onClick={() => { if (window.confirm(`Delete "${t.name}"?`)) onDelete(t.target_id); }} data-testid={`delete-target-${t.target_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="overflow-y-auto flex-1 p-5 space-y-3">
            <h4 className="font-semibold text-sm">{editing.target_id ? 'Edit Site' : 'Add a Site'}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Name *</label>
                <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="Visit Denver, KGNU…" data-testid="custom-target-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">URL *</label>
                <Input value={editing.url} onChange={e => setEditing({ ...editing, url: e.target.value })} placeholder="https://…/submit-event" data-testid="custom-target-url" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Kind</label>
                <select value={editing.kind} onChange={e => setEditing({ ...editing, kind: e.target.value })} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="custom-target-kind">
                  <option value="form">Form (manual submission)</option>
                  <option value="email">Email (we send your press release)</option>
                  <option value="api">API (automated, requires creds)</option>
                </select>
              </div>
              {editing.kind === 'email' && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Submit email *</label>
                  <Input value={editing.submit_email} onChange={e => setEditing({ ...editing, submit_email: e.target.value })} placeholder="events@example.com" data-testid="custom-target-email" />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Scope</label>
                <select value={editing.scope} onChange={e => setEditing({ ...editing, scope: e.target.value, city: e.target.value === 'national' ? '' : (editing.city || currentCity) })} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="custom-target-scope">
                  <option value="national">National</option>
                  <option value="local">Local (city-specific)</option>
                </select>
              </div>
              {editing.scope === 'local' && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">City</label>
                  <select value={editing.city || ''} onChange={e => setEditing({ ...editing, city: e.target.value })} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="custom-target-city">
                    <option value="">— pick —</option>
                    <option value="denver">Denver</option>
                    <option value="las_vegas">Las Vegas</option>
                    <option value="los_angeles">Los Angeles</option>
                    <option value="charlotte">Charlotte</option>
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Category</label>
                <Input value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} placeholder="newspaper, lifestyle, wine…" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Domain Authority (0–100)</label>
                <Input type="number" min={0} max={100} value={editing.da} onChange={e => setEditing({ ...editing, da: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Audience / notes</label>
                <Input value={editing.audience} onChange={e => setEditing({ ...editing, audience: e.target.value })} placeholder="Who reads / sees this site?" />
              </div>
              {editing.target_id && (
                <div className="col-span-2 flex items-center gap-2 mt-1">
                  <input type="checkbox" id="ct-active" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                  <label htmlFor="ct-active" className="text-xs">Active (uncheck to hide without deleting)</label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-border">
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSave} data-testid="save-custom-target-btn"><Check className="h-3.5 w-3.5 mr-1" /> Save</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
